import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { StrategyFormData } from '../../../../../../providers/backtesting/types';
import { CollapsibleSection } from '../../components/collapsible-section';
import { BacktestingInput } from '../../components/backtesting-input';

import './styles.scss';
import { InputTooltip } from '../../components/input-tooltip';

export const PortfolioSection = () => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<StrategyFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'portfolio.instruments',
  });

  const instrumentErrors = errors.portfolio?.instruments;

  const tenorTooltip = () => {
    return (
      <div className="portfolio-input-tooltip">
        <p>
          This can be constant maturity expiries, e.g. 1W (one week), 3D (three days), 1M (one
          month).
          <br />
          <br />
          Or can be exchange expiries, e.g. D0 (next expiry), D1 (expiry after next), W1 (Friday),
          W2 (Friday after next), M1 (monthly - next month end Friday), Q1 (quarterly - next quarter
          end Friday).
        </p>
      </div>
    );
  };

  const notionalTooltip = () => {
    return (
      <div>
        <p>
          If Notional Scaling is enabled, the notional will be scaled by either the PV or HWM.
          <br />
          <br />
          Further, if the Execution Style = “Average In”, the notional will also be scaled by 1.0 /
          days to expiry, meaning the full option notional will be executed over the tenor period.
          <br />
          <br />
          Additionally, if Include Weekends is disabled, the notional will be further adjusted by
          7.0 / 5.0, ensuring that seven calendar days of notional are executed across five trading
          days.
        </p>
      </div>
    );
  };

  const deltaOrStrikeTooltip = () => {
    return (
      <div>
        <p>
          Delta examples include ATMFC (at-the-money forward call), ATMFP (at-themoney forward put),
          15C, 30P, etc.
          <br />
          <br />
          Strikes can be set as absolute values (e.g., “112500”) or as offsets (e.g., “ATMFC+1000”).
        </p>
      </div>
    );
  };

  return (
    <CollapsibleSection title="Portfolio*">
      <div className="portfolio-grid">
        <div className="portfolio-header">
          <span>
            Tenor when to trade <InputTooltip content={tenorTooltip()} />
          </span>
          <span>
            Notional to be traded <InputTooltip content={notionalTooltip()} />
          </span>
          <span>
            Delta OR Strike <InputTooltip content={deltaOrStrikeTooltip()} />
          </span>
          <span />
        </div>

        {fields.map((field, index) => (
          <div className="portfolio-row" key={field.id}>
            <div className="field-group">
              <BacktestingInput
                size="fluid"
                placeholder="Tenor"
                {...register(`portfolio.instruments.${index}.tenor`)}
                errors={(instrumentErrors as any)?.[index]?.tenor?.message}
              />
            </div>
            <div className="field-group">
              <BacktestingInput
                size="fluid"
                placeholder="Notional"
                {...register(`portfolio.instruments.${index}.notional`)}
                errors={(instrumentErrors as any)?.[index]?.notional?.message}
              />
            </div>
            <div className="field-group">
              <BacktestingInput
                size="fluid"
                placeholder="Delta OR Strike"
                {...register(`portfolio.instruments.${index}.deltaOrStrike`)}
                errors={(instrumentErrors as any)?.[index]?.deltaOrStrike?.message}
              />
            </div>
            <Button
              className="btn-remove-instrument"
              onClick={() => fields.length > 1 && remove(index)}
              disabled={fields.length <= 1}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        ))}
      </div>

      <Button
        className="btn-add-instrument"
        onClick={() => append({ tenor: '', notional: '', deltaOrStrike: '' })}
      >
        <FontAwesomeIcon icon={faPlus} />
        <span>Add another instrument</span>
      </Button>
    </CollapsibleSection>
  );
};
